import { z } from "zod";
import {
  applyBuiltMessage,
  assertClientMaySetKind,
  buildMessage,
  type BuiltMessage,
  type MutateSurface,
} from "../message-builders";
import { deriveMessageKind } from "../message-kind-derivation";
import { messageKindSchema } from "../message-payload";
import type { MessageKind } from "../message-payload";
import { isThreadable } from "../message-predicates";
import { zql } from "../schema";

const messagePayloadInput = z.any().optional().nullable();

type MutatorCtx = { sub?: string } | undefined;

// Discord-shape inline reply — the row sits in the channel timeline
// citing one earlier message via `replied_to_message_id`. Distinct
// from `messageAddReplySchema` because the two intents are explicit
// at the call site, not inferred from parent state. No counter bump
// on the source — the "N replies" badge is reserved for thread
// members only.
export const messageAddInlineReplySchema = z.object({
  parent_id: z.string(),
  reply: z.object({
    id: z.string(),
    user_id: z.string(),
    content: z.string(),
    kind: messageKindSchema.optional(),
    payload: messagePayloadInput,
    is_broadcast: z.boolean().optional(),
    is_edited: z.boolean().default(false),
    mentions: z.array(z.string()).optional().nullable(),
    created_at: z.number(),
    updated_at: z.number(),
  }),
});

/**
 * Discord-shape inline reply core. Distinct from `messageAddReplyCore`
 * — the row sits in the channel timeline (NOT the thread sidebar)
 * citing the immediate clicked message via `replied_to_message_id`.
 *
 * Mode is selected at the call site, not inferred from parent state.
 * Composer's hover-bar "Reply" → this mutator. Sidebar composer's
 * "Send" → `messageAddReplyCore`.
 *
 * Differences from `messageAddReplyCore`:
 *   • Sets `replied_to_message_id = parent.id`, leaves `thread_id` null
 *     so the row passes the channel-timeline filter.
 *   • Cites the IMMEDIATE clicked message (no walk to a thread root).
 *   • Does NOT bump any counter on the source — inline replies are
 *     free-standing channel messages, not aggregated. The "N replies"
 *     badge stays reserved for thread members.
 */
export async function messageAddInlineReplyCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof messageAddInlineReplySchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  if (ctx?.sub && args.reply.user_id !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }

  const parent = await tx.run(zql.messages.where("id", args.parent_id).one());
  if (!parent) {
    if (tx.location === "server") {
      throw new Error(`Parent message not found: ${args.parent_id}`);
    }
    return null;
  }

  // Inline replies cite the immediate parent — no walk. Reject citing
  // a thread reply (those live in the sidebar; an inline citation
  // pointing into a thread would render with a quote-pill in the
  // channel that scrolls to a row not actually in the channel).
  if (parent.thread_id != null) {
    throw new Error("Cannot inline-reply to a thread reply");
  }

  if (tx.location === "server") {
    const channel = await tx.run(
      zql.channels.where("id", parent.channel_id).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot receive messages");
    }
    if (!isThreadable(parent.kind as MessageKind)) {
      throw new Error("Unauthorized: parent kind is not threadable");
    }
    // Channel-membership gate (Pattern 6) — derive channel_id from the
    // parent message. Mirrors `messageInsertCore` / `messageAddReplyCore`:
    // without it a kicked / non-member user who learns a parent message id
    // can post an inline reply into a restricted channel.
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", parent.channel_id)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }

  let kind = args.reply.kind;
  let payload = args.reply.payload ?? null;
  if (kind === undefined) {
    const derived = deriveMessageKind({
      content: args.reply.content,
      attachments: [],
    });
    kind = derived.kind;
    if (payload == null) payload = derived.payload;
  }
  assertClientMaySetKind(kind, tx.location);

  const sender = await tx.run(zql.users.where("id", args.reply.user_id).one());

  const built = buildMessage({
    id: args.reply.id,
    channel_id: parent.channel_id,
    user_id: args.reply.user_id,
    content: args.reply.content,
    kind,
    payload,
    mentions: args.reply.mentions ?? null,
    thread_id: null,
    replied_to_message_id: parent.id,
    reply_count: 0,
    reply_users: null,
    latest_reply_at: null,
    is_broadcast: args.reply.is_broadcast ?? false,
    is_edited: args.reply.is_edited,
    sender_display_name: sender?.display_name ?? null,
    sender_avatar_url: sender?.avatar_url ?? null,
    created_at: args.reply.created_at,
    updated_at: args.reply.updated_at,
  });

  await applyBuiltMessage(tx, built);
  // Intentionally NO counter bump — see JSDoc.
  return { built };
}
